/**
 * Утилиты для работы с мутациями React Query
 *
 * Централизованная логика для:
 * - Инвалидации кэша
 * - Оптимистичных обновлений
 * - Обработки ошибок
 */

import { QueryClient } from '@tanstack/react-query';
import { queryKeys } from './query-keys';

/**
 * Инвалидирует все запросы связанные с конкретной сущностью
 */
export const invalidateEntityQueries = {
  activities: (queryClient: QueryClient) => {
    return queryClient.invalidateQueries({
      queryKey: queryKeys.activities.all
    });
  },

  cars: (queryClient: QueryClient) => {
    return queryClient.invalidateQueries({
      queryKey: queryKeys.cars.all
    });
  },

  carModels: (queryClient: QueryClient) => {
    return queryClient.invalidateQueries({
      queryKey: queryKeys.carModels.all
    });
  },

  carReports: (queryClient: QueryClient) => {
    return queryClient.invalidateQueries({
      queryKey: queryKeys.carReports.all
    });
  },

  clients: (queryClient: QueryClient) => {
    return queryClient.invalidateQueries({
      queryKey: queryKeys.clients.all
    });
  },

  rentings: (queryClient: QueryClient) => {
    return queryClient.invalidateQueries({
      queryKey: queryKeys.rentings.all
    });
  },

  profile: (queryClient: QueryClient, userId: string) => {
    return Promise.all([
      queryClient.invalidateQueries({
        queryKey: queryKeys.profile.userProfile(userId)
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.profile.employeeProfile(userId)
      }),
    ]);
  },
};

/**
 * Инвалидирует конкретную детальную запись и список
 */
export const invalidateDetailAndList = async (
  queryClient: QueryClient,
  entityType: keyof typeof queryKeys,
  detailId?: string
) => {
  const entity = queryKeys[entityType] as any;

  if (!entity || !entity.all) {
    console.warn(`Invalid entity type: ${entityType}`);
    return;
  }

  // Инвалидируем весь список
  await queryClient.invalidateQueries({
    queryKey: entity.all
  });

  // Если есть ID, инвалидируем конкретную запись
  if (detailId && entity.detail) {
    await queryClient.invalidateQueries({
      queryKey: entity.detail(detailId)
    });
  }
};

/**
 * Опции для стандартных мутаций
 */
export interface StandardMutationOptions<TData = unknown, TError = Error, TVariables = unknown> {
  onSuccess?: (data: TData, variables: TVariables) => void | Promise<void>;
  onError?: (error: TError, variables: TVariables) => void;
  showSuccessMessage?: boolean;
  showErrorMessage?: boolean;
}

/**
 * Создает стандартные обработчики для мутации с инвалидацией
 */
export const createMutationHandlers = <TData = unknown, TError = Error, TVariables = unknown>(
  queryClient: QueryClient,
  entityType: keyof typeof queryKeys,
  options?: StandardMutationOptions<TData, TError, TVariables>
) => {
  return {
    onSuccess: async (data: TData, variables: TVariables) => {
      // Инвалидируем кэш
      const entity = queryKeys[entityType] as any;
      if (entity && entity.all) {
        await queryClient.invalidateQueries({ queryKey: entity.all });
      }

      // Вызываем пользовательский обработчик
      if (options?.onSuccess) {
        await options.onSuccess(data, variables);
      }
    },
    onError: (error: TError, variables: TVariables) => {
      // Логируем ошибку
      console.error(`Mutation error for ${entityType}:`, error);

      // Вызываем пользовательский обработчик
      if (options?.onError) {
        options.onError(error, variables);
      }
    },
  };
};
